// One table for the partner, startup and F26 pipelines. Stage means the relationship with ernd. Filters live in the URL.
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../../../lib'
import { usePaletteActions } from '../app/Palette'
import { Busy, Button, Copy, Err, Page, Strip, Table, dayOf, useCopy, useCursor, useKeys, useKit, type Column, since } from '../kit'
import { useRecordActions } from '../records/actions'
import { BLOCKED_STAGES, REASON_MIN, holdWord, nameOf, queryOf, rememberList, routeWord, safeHref, tierWord, useFilters, usePaged, useWrite, type Imported, type Rel, type StageDef, type Sync, type Track } from '../records/lib'
import { Check, FilterBar, LoadMore, ReadLine, SearchField, Select, lastTouch, pageCheck } from '../records/parts'

type Row = Rel & {
  company: string; funding_stage: string | null; person: string | null; warm: string[]; intro_routes: number
  last_at: string | null; last_actor: string | null; last_channel: string | null; last_summary: string | null; task_title: string | null; task_due: string | null
  next_at: string | null; next_summary: string | null
  page_url: string | null; page_checked_at: string | null; page_check_status: number | null; opened: string | null; scrolled: string | null; active: string | null
}
type Data = {
  at: string; track: Track; rows: Row[]; truncated: boolean; next: string | null
  stages: StageDef[]; counts: Record<string, number>; total: number; active: number; nobody: number; sync: Sync; lists: string[]; pages_checked_at: string | null; tracker_read_at: string | null; imported: Imported; unmessaged: number
  options: { routes: string[]; tiers: string[]; funding: string[] }
}

const TRACKS: Record<string, { track: Track; title: string }> = {
  partners: { track: 'partner', title: 'Partners' }, startups: { track: 'startup', title: 'Startups' }, f26: { track: 'f26', title: 'F26' },
}
const FILTERS = ['q', 'owner', 'route', 'tier', 'funding', 'in_range', 'has_page', 'untouched', 'warm', 'internal', 'unmessaged']
const CHANNELS = [{ value: 'linkedin', label: 'LinkedIn' }, { value: 'email', label: 'Email' }, { value: 'bookface', label: 'Bookface' }, { value: 'chat', label: 'Chat' }, { value: 'other', label: 'Other' }]

const opt = (vals: string[] | undefined, any: string) => [{ value: '', label: any }, ...(vals ?? []).map((v) => ({ value: v, label: v }))]

export function PipelinePage() {
  const { track: slug = '' } = useParams()
  const t = TRACKS[slug]
  if (!t) return <Page title="Pipeline"><Err error={new Error('No such pipeline.')} /></Page>
  return <Pipeline key={t.track} track={t.track} title={t.title} />
}

function Pipeline({ track, title }: { track: Track; title: string }) {
  const { sp, get, put } = useFilters()
  const navigate = useNavigate()
  const { toast } = useKit()
  const copy2 = useCopy()
  const write = useWrite()
  const view = get('stage') || 'active'    // Active by default. All is one click away
  const search = queryOf(sp, FILTERS, view === 'all' ? {} : { stage: view })
  const q = usePaged<Data, Row>(['crm', 'pipeline', track, search], `/api/staff/crm/pipeline/${track}${search}`, 60_000)
  const d = q.head
  const rows = q.rows
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [showAll, setShowAll] = useState(false)
  const lastPick = useRef<string | null>(null)
  useEffect(() => { if (rows.length) rememberList('company', [...new Set(rows.map((r) => r.company_id))]) }, [rows])
  const open = (r: Row) => navigate(`/staff/companies/${r.company_id}`)
  const { cur, row, setCur } = useCursor(rows, (r) => r.id, open)
  const a = useRecordActions({ companyId: row?.company_id, personId: row?.person_id })
  const stagesFor = d?.stages ?? []

  const copy = async (r: Row | null) => {
    if (!r) return
    if (!r.page_url) { toast('No page yet', { bad: true }); return }
    await copy2(r.page_url, 'Link copied')
  }
  const flip = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  /** Shift extends from the last row picked. */
  const pick = (id: string, shift: boolean) => {
    const from = rows.findIndex((r) => r.id === lastPick.current)
    const to = rows.findIndex((r) => r.id === id)
    if (shift && from >= 0 && to >= 0) {
      const ids = rows.slice(Math.min(from, to), Math.max(from, to) + 1).map((r) => r.id)
      setPicked((s) => new Set([...s, ...ids]))
    } else flip(id)
    lastPick.current = id
  }
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id))
  const pickAll = (on: boolean) => setPicked(on ? new Set(rows.map((r) => r.id)) : new Set())
  const live = rows.filter((r) => picked.has(r.id))

  useKeys({
    s: () => { if (row) void a.stage([row]) },
    o: () => { if (row) void a.owner([row]) },
    n: () => { if (row) void a.note({ companyId: row.company_id, personId: row.person_id }) },
    c: () => void copy(row),
    x: () => { if (row) flip(row.id) },
  })
  usePaletteActions('pipeline', row ? [
    { id: 'stage', label: `Change stage: ${row.company}`, hint: 's', run: () => void a.stage([row]) },
    { id: 'owner', label: `Set owner: ${row.company}`, hint: 'o', run: () => void a.owner([row]) },
    { id: 'note', label: `Add a note: ${row.company}`, hint: 'n', run: () => void a.note({ companyId: row.company_id, personId: row.person_id }) },
    { id: 'copy', label: `Copy page link: ${row.company}`, hint: 'c', run: () => void copy(row) },
  ] : [])

  const bulk = async (patch: Record<string, unknown>, ok: (r: { queued: number; yc_left_off: number }) => string) => {
    const r = await write<{ changed: number; queued: number; yc_left_off: number }>('POST', '/api/staff/crm/bulk', { relationship_ids: live.map((x) => x.id), ...patch })
    if (r) { toast(ok(r)); setPicked(new Set()) }
  }
  const bulkOwner = async () => {
    const v = await a.open({ title: `Set owner on ${live.length}`, confirm: 'Set owner', fields: [{ key: 'owner', label: 'Owner', kind: 'select', value: a.owners[0], required: true, options: a.owners.map((o) => ({ value: o, label: nameOf(o) })) }] })
    if (v) await bulk({ owner: v.owner }, () => `Owner set on ${live.length}`)
  }
  const bulkStage = async () => {
    const out = live.some((r) => BLOCKED_STAGES.has(r.stage))
    const v = await a.open({
      title: `Change stage on ${live.length}`, confirm: 'Change stage', fields: [
        { key: 'stage', label: 'Stage', kind: 'select', value: stagesFor[0]?.key ?? '', required: true, options: stagesFor.map((s) => ({ value: s.key, label: s.label })) },
        { key: 'reason', label: 'Why', kind: 'long', min: REASON_MIN, required: (x) => out || BLOCKED_STAGES.has(x.stage) },
      ],
    })
    if (v) await bulk({ stage: v.stage, reason: v.reason.trim() || null }, () => `Stage changed on ${live.length}`)
  }
  const bulkList = async () => {
    const lists = d?.lists ?? []
    const v = await a.open({
      title: `Add ${live.length} to a send list`, confirm: 'Add to list',
      body: 'This queues them. Nothing is sent.',
      fields: [
        { key: 'existing', label: 'List', kind: 'select', value: lists[0] ?? '', options: [...lists.map((l) => ({ value: l, label: l })), { value: '', label: 'New list' }] },
        { key: 'list', label: 'New list name', required: (x) => !x.existing, when: (x) => !x.existing },
        { key: 'channel', label: 'Channel', kind: 'select', value: 'linkedin', required: true, options: CHANNELS },
        { key: 'message', label: 'Message. {first}, {company} and {page} fill in', kind: 'long' },
      ],
    })
    if (v) await bulk({ add_to_list: v.existing || v.list.trim(), channel: v.channel, message: v.message.trim() || null }, (r) => `Queued ${r.queued}${r.yc_left_off ? `, ${r.yc_left_off} YC left off a cold list` : ''}`)
  }

  const dash = <span className="st-faint">–</span>
  const allColumns: Column<Row>[] = [
    { key: 'company', head: <span className="st-pick-row"><label className="st-pick-hit"><input type="checkbox" aria-label="Select all on this page" checked={allPicked} data-testid="st-pick-all" onChange={(e) => pickAll(e.target.checked)} /></label>Company</span>, label: 'Company', primary: true, cell: (r) => (
      <span className="st-pick-row">
        <label className="st-pick-hit"><input type="checkbox" aria-label={`Select ${r.company}`} checked={picked.has(r.id)} data-testid="st-pick" onChange={() => undefined} onClick={(e) => pick(r.id, e.shiftKey)} /></label>
        <span><Link className="st-strong" to={`/staff/companies/${r.company_id}`}>{r.company}</Link>{holdWord(r) ? <div className="st-warn" data-testid="st-blocked">{holdWord(r)}</div> : null}</span>
      </span>
    ) },
    { key: 'person', head: 'Person', cell: (r) => (r.person || r.warm.length ? (
      <span className="st-last">
        {r.person ? <span>{r.person_id ? <Link to={`/staff/people/${r.person_id}`}>{r.person}</Link> : r.person}</span> : null}
        {r.warm.length ? <span className="st-last-sub" data-testid="st-warm-row">{r.warm.map(nameOf).join(', ')} {r.warm.length > 1 ? 'know' : 'knows'} them</span> : null}
      </span>
    ) : track === 'partner' ? <span className="st-muted" data-testid="st-no-contact">No contact on record</span> : dash) },
    { key: 'page', head: 'Page', phone: false, cell: (r) => (safeHref(r.page_url) ? <span className="st-conn-acts"><a className="st-link" href={r.page_url!} target="_blank" rel="noreferrer" title={pageCheck({ checked_at: r.page_checked_at, check_status: r.page_check_status, state: '' }) || undefined}>Open</a><Copy text={r.page_url!} testid="st-copy-page" /></span> : dash) },
    { key: 'owner', head: 'Owner', phone: false, nowrap: true, cell: (r) => nameOf(r.owner) || dash },
    { key: 'stage', head: 'Stage', nowrap: true, cell: (r) => (r.stage_from ? `${r.stage_label}, from ${r.stage_from}` : r.stage_label) },
    { key: 'funding', head: 'Funding', phone: false, nowrap: true, cell: (r) => r.funding_stage ?? dash },
    { key: 'route', head: 'Route', phone: false, cell: (r) => (r.route || r.intro_routes ? <span>{routeWord(r.route)}{r.intro_routes ? <span className="st-muted"> {r.intro_routes} intro{r.intro_routes > 1 ? 's' : ''}</span> : null}</span> : dash) },
    { key: 'tier', head: 'Tier', phone: false, nowrap: true, cell: (r) => tierWord(r.tier) ?? dash },
    { key: 'last', head: 'Last touch', cell: (r) => lastTouch(r) ?? dash },
    { key: 'opened', head: 'Opened', phone: false, cell: (r) => r.opened ?? dash },
    { key: 'scrolled', head: 'Scrolled', phone: false, cell: (r) => r.scrolled ?? dash },
    { key: 'active', head: 'Active', phone: false, cell: (r) => r.active ?? dash },
    { key: 'task', head: 'Next', phone: false, cell: (r) => (r.task_title ? <span className="st-last"><span>{r.task_title}</span>{r.task_due ? <span className="st-last-sub">{dayOf(r.task_due)}</span> : null}</span>
      : r.next_at ? <span className="st-last" data-testid="st-next-booking"><span>{r.next_summary ?? 'Booked'}</span><span className="st-last-sub">{dayOf(r.next_at)}</span></span> : dash) },
  ]
  // A column nothing in the list fills says nothing, so it goes. Otherwise an unread cell is a dash.
  const filled: Record<string, (r: Row) => unknown> = { funding: (r) => r.funding_stage, route: (r) => r.route ?? (r.intro_routes || null), tier: (r) => r.tier, opened: (r) => r.opened, scrolled: (r) => r.scrolled,
    active: (r) => r.active, task: (r) => r.task_title ?? r.next_at, page: (r) => r.page_url, owner: (r) => r.owner }
  // Under 5% filled says nothing either.
  const columns = allColumns.filter((c) => !filled[c.key] || rows.filter((r) => filled[c.key](r)).length >= Math.max(1, rows.length * 0.05))

  const allCount = d ? Object.values(d.counts).reduce((x, y) => x + y, 0) : undefined
  const stageItems = [
    { key: 'active', label: 'Active', n: d?.active },
    { key: 'all', label: 'All', n: allCount },
    ...stagesFor.filter((s) => showAll || view === s.key || (d?.counts[s.key] ?? 0) > 0).map((s) => ({ key: s.key, label: s.label, n: d ? (d.counts[s.key] ?? 0) : undefined })),
  ]
  const zero = stagesFor.filter((s) => !(d?.counts[s.key])).length
  const on = FILTERS.filter((k) => k !== 'q' && sp.get(k)).length
  const filtered = FILTERS.some((k) => sp.get(k))

  return (
    <Page title={title} testid="st-pipeline" sub={<ReadLine at={d?.at} sync={d?.sync} what="Pipeline" pages={d ? d.pages_checked_at : undefined} imported={d?.imported} extra={d?.tracker_read_at && rows.some((r) => r.opened || r.scrolled || r.active) ? [`Opened, Scrolled and Active from the tracker ${since(d.tracker_read_at)}`] : undefined} />}>
      {track === 'f26' && d ? (
        <p className="st-note" data-testid="st-plain"><button type="button" className="st-inline" data-testid="st-plain-link" onClick={() => put({ unmessaged: '1', stage: 'all' })}>{d.unmessaged.toLocaleString('en-US')} of {(allCount ?? 0).toLocaleString('en-US')} not messaged yet</button>, once Non-US and Off are out</p>
      ) : null}
      <div className="st-swipe st-row">
        <Strip items={stageItems} value={view} onChange={(k) => put({ stage: k === 'active' ? null : k })} testid="st-stages" />
        {zero ? <Button tone="quiet" data-testid="st-show-stages" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Hide empty' : 'Show all'}</Button> : null}
      </div>
      <FilterBar
        on={on}
        search={<SearchField value={get('q')} onChange={(v) => put({ q: v })} placeholder="Search company or person" testid="st-q" />}
        more={(
          <>
            <Select label="Route" value={get('route')} testid="st-f-route" onChange={(v) => put({ route: v })} options={[{ value: '', label: 'Any route' }, ...(d?.options.routes ?? []).map((v) => ({ value: v, label: routeWord(v) }))]} />
            <Select label="Tier" value={get('tier')} testid="st-f-tier" onChange={(v) => put({ tier: v })} options={[{ value: '', label: 'Any tier' }, ...(d?.options.tiers ?? []).map((v) => ({ value: v, label: tierWord(v) ?? v }))]} />
            <Select label="Funding" value={get('funding')} testid="st-f-funding" onChange={(v) => put({ funding: v })} options={opt(d?.options.funding, 'Any funding')} />
            <Select label="In range" value={get('in_range')} testid="st-f-range" onChange={(v) => put({ in_range: v })} options={[{ value: '', label: 'In range or not' }, { value: '1', label: 'In range' }, { value: '0', label: 'Out of range' }]} />
            <Select label="Page" value={get('has_page')} testid="st-f-page" onChange={(v) => put({ has_page: v })} options={[{ value: '', label: 'Page or not' }, { value: '1', label: 'Has a page' }, { value: '0', label: 'No page' }]} />
            <Check label="Untouched" value={get('untouched') === '1'} testid="st-f-untouched" onChange={(v) => put({ untouched: v ? '1' : null })} />
            <Check label="Warm" value={get('warm') === '1'} testid="st-f-warm" onChange={(v) => put({ warm: v ? '1' : null })} />
            <Check label="Show internal" value={get('internal') === '1'} testid="st-f-internal" onChange={(v) => put({ internal: v ? '1' : null })} />
            {filtered ? <Button tone="quiet" data-testid="st-f-clear" onClick={() => put(Object.fromEntries(FILTERS.map((k) => [k, null])))}>Clear</Button> : null}
          </>
        )}
      >
        <Select label="Owner" value={get('owner')} testid="st-f-owner" onChange={(v) => put({ owner: v })} options={[{ value: '', label: 'Any owner' }, { value: 'none', label: 'Nobody' }, ...a.owners.map((o) => ({ value: o, label: nameOf(o) }))]} />
        {d ? <button type="button" className="st-inline" data-testid="st-nobody" onClick={() => put({ owner: 'none' })}>{d.nobody.toLocaleString('en-US')} with no owner</button> : null}
      </FilterBar>
      {live.length ? (
        <div className="st-bulk" data-testid="st-bulk">
          <span>{live.length} selected</span>
          <Button onClick={() => void bulkOwner()} data-testid="st-bulk-owner">Set owner</Button>
          <Button onClick={() => void bulkStage()} data-testid="st-bulk-stage">Set stage</Button>
          <Button onClick={() => void bulkList()} data-testid="st-bulk-list">Add to send list</Button>
          {!allPicked ? <Button tone="quiet" onClick={() => pickAll(true)} data-testid="st-bulk-all">Select all {rows.length}</Button> : null}
          <Button tone="quiet" onClick={() => setPicked(new Set())}>Clear</Button>
        </div>
      ) : null}
      {q.error ? <Err error={q.error} /> : !d ? <Busy /> : (
        <>
          <Table rows={rows} columns={columns} rowKey={(r) => r.id} cur={cur} onPick={(r) => setCur(r.id)} onOpen={open} testid="st-pipeline-table" empty="Nothing matches these filters." />
          <LoadMore shown={rows.length} total={d.total} more={q.more} loading={q.loading} load={q.load} />
        </>
      )}
      {a.node}
    </Page>
  )
}
